import {css} from 'aphrodite';
import React, {useEffect, useMemo, useCallback} from 'react';
import {HandleType} from 'reactflow';

import {assertAndContinue} from 'Asserts';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {
  NODE_HEADER_STYLES,
  NODE_STYLES,
  MAX_NODE_STATUS_LENGTH,
  noDragClass,
} from '../BenchlingNodeDesignConstants';
import {NodeValidationState, NodeHandleProps, NodeIcon, NodeSection} from '../BenchlingNodeTypes';

const NodeValidationStateIcon: Record<NodeValidationState, BenchlingIconName> = {
  WARNING: 'exclamation-triangle',
  ERROR: 'circle-xmark',
};

function BenchlingNodeHeaderNodeIcon(props: {nodeIcon?: NodeIcon}): JSX.Element | null {
  if (!props.nodeIcon) {
    return null;
  }
  const baseProps = {
    className: css(NODE_HEADER_STYLES.nodeIcon),
    dataTestAttrs: {
      component: 'BenchlingNodeHeader',
      element: 'nodeIcon',
    },
  };

  let icon;
  let style;
  if (typeof props.nodeIcon === 'string') {
    icon = props.nodeIcon;
    style = {};
  } else {
    icon = props.nodeIcon.icon;
    style = props.nodeIcon.color ? {color: props.nodeIcon.color} : {};
  }

  return <BenchlingIcon {...baseProps} icon={icon} style={style} />;
}

function BenchlingNodeHeaderNodeName(props: {nodeName: string}): JSX.Element {
  return (
    <div
      className={css(NODE_HEADER_STYLES.nodeName)}
      {...getTestProps({
        component: 'BenchlingNodeHeader',
        element: 'nodeName',
      })}
    >
      {props.nodeName}
    </div>
  );
}

function BenchlingNodeHeaderNodeStatus(props: {nodeStatus?: string}): JSX.Element | null {
  return props.nodeStatus ? (
    <div
      className={css(NODE_HEADER_STYLES.nodeStatus)}
      {...getTestProps({
        component: 'BenchlingNodeHeader',
        element: 'nodeStatus',
      })}
    >
      {props.nodeStatus.toUpperCase()}
    </div>
  ) : null;
}

function BenchlingNodeHeaderNodeValidationStateIcon(props: {
  nodeValidationState?: NodeValidationState;
}): JSX.Element | null {
  return props.nodeValidationState ? (
    <BenchlingIcon
      className={css(
        NODE_HEADER_STYLES.nodeValidationState,
        props.nodeValidationState === NodeValidationState.WARNING && NODE_STYLES.warningIcon,
        props.nodeValidationState === NodeValidationState.ERROR && NODE_STYLES.errorIcon
      )}
      icon={NodeValidationStateIcon[props.nodeValidationState]}
      dataTestAttrs={{
        component: 'BenchlingNodeHeader',
        element: 'nodeValidationStateIcon',
      }}
    />
  ) : null;
}

function BenchlingNodeHeaderNodeSecondaryInfo(props: {nodeSecondaryInfo?: string}): JSX.Element | null {
  return props.nodeSecondaryInfo ? (
    <div
      className={css(NODE_HEADER_STYLES.secondaryInfo)}
      {...getTestProps({
        component: 'BenchlingNodeHeader',
        element: 'nodeSecondaryInfo',
      })}
    >
      {props.nodeSecondaryInfo}
    </div>
  ) : null;
}

function BenchlingNodeHeaderHandle(props: {
  nodeId: string;
  handleType: HandleType;
  nodeHeaderHandleRenderer?: (props: NodeHandleProps) => JSX.Element | null;
}): JSX.Element | null {
  if (!props.nodeHeaderHandleRenderer) {
    return null;
  }
  return (
    <div className={noDragClass()}>
      {props.nodeHeaderHandleRenderer?.({
        nodeId: props.nodeId,
        handleType: props.handleType,
      })}
    </div>
  );
}

/**
 * Header for the BenchlingNode.
 */
export function BenchlingNodeHeader<TRowData>(props: {
  id: string;
  data: {
    nodeName: string;
    sections: Array<NodeSection<TRowData>>;
    nodeIcon?: NodeIcon;
    nodeStatus?: string;
    nodeValidationState?: NodeValidationState;
    nodeSecondaryInfo?: string;
    nodeHeaderHandleRenderer?: (props: NodeHandleProps) => JSX.Element | null;
    hideSectionSummaries?: boolean;
    headerColor?: string;
    onNodeHeaderClick?: (nodeId: string) => void;
  };
}): JSX.Element {
  useEffect(() => {
    assertAndContinue(
      (props.data.nodeStatus ?? '').length <= MAX_NODE_STATUS_LENGTH,
      `Status text should be less than ${MAX_NODE_STATUS_LENGTH} characters`
    );
  }, [props.data.nodeStatus]);

  const handleNodeHeaderClick = useCallback(() => {
    if (props.data.onNodeHeaderClick) {
      props.data.onNodeHeaderClick(props.id);
    }
  }, [props.data, props.id]);

  const hasContentBelowHeader = useMemo(
    () => !props.data.hideSectionSummaries || props.data.sections.some(({hideSection}) => !hideSection),
    [props.data.hideSectionSummaries, props.data.sections]
  );

  return (
    <div
      className={css(
        NODE_HEADER_STYLES.nodeHeader,
        hasContentBelowHeader && NODE_HEADER_STYLES.nodeHeaderBorder
      )}
      style={props.data.headerColor ? {backgroundColor: props.data.headerColor} : {}}
      {...getTestProps({
        component: 'BenchlingNodeHeader',
        element: 'root',
      })}
      onClick={handleNodeHeaderClick}
    >
      <div className={css(NODE_HEADER_STYLES.nodeHeaderContent)}>
        <div className={css(NODE_HEADER_STYLES.nodeHeaderTopRow)}>
          <div className={css(NODE_HEADER_STYLES.nodeHeaderTopRowLeft)}>
            <BenchlingNodeHeaderNodeIcon nodeIcon={props.data.nodeIcon} />
            <BenchlingNodeHeaderNodeName nodeName={props.data.nodeName} />
          </div>
          <div className={css(NODE_HEADER_STYLES.nodeHeaderTopRowRight)}>
            <BenchlingNodeHeaderNodeStatus nodeStatus={props.data.nodeStatus} />
            <BenchlingNodeHeaderNodeValidationStateIcon
              nodeValidationState={props.data.nodeValidationState}
            />
          </div>
        </div>
        <BenchlingNodeHeaderNodeSecondaryInfo nodeSecondaryInfo={props.data.nodeSecondaryInfo} />
      </div>
      <div>
        <BenchlingNodeHeaderHandle
          nodeId={props.id}
          handleType="target"
          nodeHeaderHandleRenderer={props.data.nodeHeaderHandleRenderer}
        />
        <BenchlingNodeHeaderHandle
          nodeId={props.id}
          handleType="source"
          nodeHeaderHandleRenderer={props.data.nodeHeaderHandleRenderer}
        />
      </div>
    </div>
  );
}
